// Product results as rendered by both the `predictive-search` section and the
// `predictive-search-compatibility` fallback. Used to tell an empty result set
// apart so a broader query can be tried before giving up.
const PREDICTIVE_SEARCH_PRODUCT_RESULT_SELECTOR =
  '#predictive-search-results-products-list li, .predictive-search__products-column .grid__item';

// Shopify's search index matches only from the start of a word, so a fragment
// taken from the middle of one ("טיין" inside "מאונטיין") is unreachable by any
// query. This keeps the product titles in the browser, from the
// /collections/all?view=search-index feed, so such a fragment can be turned
// back into the whole words that Shopify *can* find.
const PredictiveSearchTitleIndex = {
  // Bump when the shape of the cached payload changes.
  CACHE_KEY: 'predictive-search-title-index-v1',
  CACHE_TTL: 12 * 60 * 60 * 1000,
  // 50 is the most `paginate` accepts, so this caps the index at 2,000 products.
  // Raise it for a larger catalog — each page is only a few KB of titles.
  MAX_PAGES: 40,
  CONCURRENCY: 4,
  // How long a search waits on a half-built index before using what it has.
  READY_TIMEOUT: 1200,
  MAX_EXPANSIONS: 4,

  titles: null,
  normalized: null,
  loadPromise: null,

  normalize(value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      // Latin accents and Hebrew niqqud are rarely typed into a search box.
      .replace(/[\u0300-\u036F\u0591-\u05C7]/g, '')
      // Fold Hebrew final letters so a fragment can match inside a word.
      .replace(/ך/g, 'כ')
      .replace(/ם/g, 'מ')
      .replace(/ן/g, 'נ')
      .replace(/ף/g, 'פ')
      .replace(/ץ/g, 'צ');
  },

  splitWords(value) {
    return value.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  },

  reset() {
    this.titles = [];
    this.normalized = [];
  },

  addTitle(title) {
    this.titles.push(title);
    this.normalized.push(this.normalize(title));
  },

  // The whole words of one title that contain the fragment, in their original
  // spelling — that is what Shopify's own index holds.
  wordsContaining(position, fragment) {
    return this.splitWords(this.titles[position]).filter((word) => this.normalize(word).includes(fragment));
  },

  expand(searchTerm) {
    if (!this.titles || !this.titles.length) return null;

    const fragments = this.splitWords(searchTerm)
      .map((word) => this.normalize(word))
      // A single character sits inside almost every title, so it says nothing
      // about which word was meant.
      .filter((fragment) => fragment.length > 1);
    if (!fragments.length) return null;

    const matching = [];
    this.normalized.forEach((title, position) => {
      if (fragments.every((fragment) => title.includes(fragment))) matching.push(position);
    });
    if (!matching.length) return null;

    // A single fragment can sit inside several unrelated words, so search for
    // all of them; shortest first, being the closest to what was typed.
    if (fragments.length === 1) {
      const found = new Map();
      matching.forEach((position) => {
        this.wordsContaining(position, fragments[0]).forEach((word) => {
          const key = this.normalize(word);
          if (!found.has(key)) found.set(key, word);
        });
      });

      return (
        Array.from(found.values())
          .sort((a, b) => a.length - b.length)
          .slice(0, this.MAX_EXPANSIONS)
          .join(' OR ') || null
      );
    }

    // With several words typed, one title has to satisfy them all — take the
    // shortest such title and read the whole word back for each fragment, so
    // the words are ones that genuinely occur together.
    const best = matching.sort((a, b) => this.normalized[a].length - this.normalized[b].length)[0];
    const words = fragments.map((fragment) => this.wordsContaining(best, fragment)[0]).filter(Boolean);

    return words.length === fragments.length ? words.join(' ') : null;
  },

  fetchPage(page) {
    return fetch(`${window.Shopify.routes.root}collections/all?view=search-index&page=${page}`).then(
      (response) => {
        if (!response.ok) throw new Error(response.status);

        return response.json();
      }
    );
  },

  restore() {
    let cached = null;

    try {
      cached = JSON.parse(window.localStorage.getItem(this.CACHE_KEY));
    } catch (error) {
      return null;
    }

    if (!cached || !Array.isArray(cached.titles) || Date.now() - cached.at > this.CACHE_TTL) return null;

    this.reset();
    cached.titles.forEach((title) => this.addTitle(title));

    return Promise.resolve(this);
  },

  persist() {
    try {
      window.localStorage.setItem(this.CACHE_KEY, JSON.stringify({ at: Date.now(), titles: this.titles }));
    } catch (error) {
      // Storage full or blocked (private browsing) — the index still works in memory.
    }
  },

  fetchAll() {
    this.reset();

    return this.fetchPage(1)
      .then((firstPage) => {
        firstPage.titles.forEach((title) => this.addTitle(title));

        const lastPage = Math.min(firstPage.pages || 1, this.MAX_PAGES);
        const queue = [];
        for (let page = 2; page <= lastPage; page += 1) queue.push(page);

        const worker = () => {
          const page = queue.shift();
          if (!page) return Promise.resolve();

          return this.fetchPage(page)
            .then((data) => data.titles.forEach((title) => this.addTitle(title)))
            // A page that fails to load only narrows the index, it doesn't break it.
            .catch(() => {})
            .then(worker);
        };

        return Promise.all(Array.from({ length: this.CONCURRENCY }, worker));
      })
      .then(() => {
        this.persist();
        return this;
      })
      // Without an index, search simply falls back to Shopify's own matching.
      .catch(() => this);
  },

  load() {
    if (!this.loadPromise) this.loadPromise = this.restore() || this.fetchAll();

    return this.loadPromise;
  },

  // Don't hold a search hostage to a catalog that is still downloading — a
  // partially built index already covers most of what customers type.
  ready() {
    return Promise.race([
      this.load(),
      new Promise((resolve) => setTimeout(() => resolve(this), this.READY_TIMEOUT)),
    ]);
  },
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.closest(".details").querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    const focusOutTarget = this.closest('.details') || this;
    focusOutTarget.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this._onOutsideClick = (e) => {
      if (!this.isOpen) return;
      const container = this.closest('.details') || this;
      if (e.target.classList.contains('modal-overlay') || !container.contains(e.target)) {
        this.close();
      }
    };
    document.addEventListener('click', this._onOutsideClick, true);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    // Start building the title index now so it is ready by the time a partial
    // word actually needs expanding.
    PredictiveSearchTitleIndex.load();

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // On mobile, dismissing the keyboard moves focus to document.body — not a real
      // "clicked outside" event. The touchstart listener on document handles that case.
      if (document.activeElement === document.body) return;
      const container = this.closest('.details');
      if (container) {
        if (!container.contains(document.activeElement)) this.close();
      } else {
        if (!this.contains(document.activeElement)) this.close();
      }
    });
  }

  onKeyup(event) {   
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }


  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }



  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const escapedPrevious = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // `match` returns null when the button still shows an older term, which
      // happens while the customer types faster than the results come back.
      const previousMatches = currentButtonText.match(new RegExp(escapedPrevious, "g"));
      if (previousMatches && previousMatches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  supportPredictiveApi() {
    const shopifyFeatureRequests = JSON.parse(document.getElementById("shopify-features").textContent);
    return shopifyFeatureRequests["predictiveSearch"];
  }

  // Shopify's search index only matches from the start of a word, and the
  // storefront Search API prefixes the *last* term only. Giving every term an
  // explicit trailing wildcard makes each partial word match on its own, so
  // typing "מאונט" already reaches products titled "מאונטיין".
  buildPartialMatchQuery(searchTerm) {
    // Leave hand-written search syntax (operators, quotes, wildcards) untouched.
    if (/["()*~]|(^|\s)(AND|OR|NOT)(\s|$)/.test(searchTerm)) return searchTerm;

    return searchTerm
      .split(/\s+/)
      .filter(Boolean)
      // Single characters are wildcarded too, so a lone "נ" still opens the
      // dropdown. The result list is capped by the section, so the broad match
      // costs nothing beyond less useful ordering.
      .map((word) => `${word}*`)
      .join(' ');
  }

  buildPredictiveSearchUrl(searchTerm, widenFields) {
    const params = new URLSearchParams({ q: searchTerm });

    if (widenFields) {
      // Widen the fields a partial word is tested against so a fragment can also
      // hit a variant title, vendor or tag — not just the product title.
      params.set('resources[type]', 'query,product,collection,page,article');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
      params.set('resources[options][unavailable_products]', 'last');
    }

    params.set('section_id', 'predictive-search');

    return `${routes.predictive_search_url}?${params}`;
  }

  buildCompatibilitySearchUrl(searchTerm) {
    const params = new URLSearchParams({
      q: searchTerm,
      type: 'product',
      'options[unavailable_products]': 'last',
      'options[prefix]': 'last',
      section_id: 'predictive-search-compatibility',
    });

    return `${window.Shopify.routes.root}search?${params}`;
  }

  fetchResultsSection(url, sectionId) {
    return fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${sectionId}`);

        if (!section) throw new Error(`Missing markup for #shopify-section-${sectionId}`);

        return section;
      });
  }

  // Last resort when Shopify found nothing: the fragment may sit in the middle
  // of a word, which its index cannot match. Turn it into whole words first.
  expandWithTitleIndex(searchTerm, section, sectionId) {
    if (section.querySelector(PREDICTIVE_SEARCH_PRODUCT_RESULT_SELECTOR)) return Promise.resolve(section);

    return PredictiveSearchTitleIndex.ready().then((index) => {
      if (this.searchTerm !== searchTerm) return section;

      const expanded = index.expand(searchTerm);
      if (!expanded || expanded === searchTerm) return section;

      const url =
        sectionId === 'predictive-search'
          ? this.buildPredictiveSearchUrl(expanded, false)
          : this.buildCompatibilitySearchUrl(expanded);

      return this.fetchResultsSection(url, sectionId).then(
        (expandedSection) =>
          expandedSection.querySelector(PREDICTIVE_SEARCH_PRODUCT_RESULT_SELECTOR) ? expandedSection : section,
        (error) => {
          if (error?.name === 'AbortError' || error?.code === 20) throw error;

          return section;
        }
      );
    });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/ /g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Drop a request still in flight for an earlier term so its now-stale markup
    // can't land on top of the results for what the customer is typing now.
    this.abortController.abort();
    this.abortController = new AbortController();

    const usePredictiveApi = this.supportPredictiveApi();
    const sectionId = usePredictiveApi ? 'predictive-search' : 'predictive-search-compatibility';

    // Each entry is tried in turn, moving on when the previous one errored or
    // came back without a single product. The Predictive Search API matches
    // partial words itself, so there the fallback only drops the widened field
    // list; the Search API needs the wildcard query to match partial words at all.
    const candidates = usePredictiveApi
      ? [
          this.buildPredictiveSearchUrl(searchTerm, true),
          this.buildPredictiveSearchUrl(searchTerm, false),
        ]
      : [
          this.buildCompatibilitySearchUrl(this.buildPartialMatchQuery(searchTerm)),
          this.buildCompatibilitySearchUrl(searchTerm),
        ];
    const urls = candidates.filter((url, index) => candidates.indexOf(url) === index);

    const runAttempt = (index) => {
      const isLastAttempt = index === urls.length - 1;

      return this.fetchResultsSection(urls[index], sectionId).then(
        (section) => {
          if (!isLastAttempt && !section.querySelector(PREDICTIVE_SEARCH_PRODUCT_RESULT_SELECTOR)) {
            return runAttempt(index + 1);
          }

          return section;
        },
        (error) => {
          if (isLastAttempt || error?.name === 'AbortError' || error?.code === 20) throw error;

          return runAttempt(index + 1);
        }
      );
    };

    runAttempt(0)
      .then((section) => this.expandWithTitleIndex(searchTerm, section, sectionId))
      .then((section) => {
        // The customer kept typing while this was in flight — the newer request wins.
        if (this.searchTerm !== searchTerm) return;

        const resultsMarkup = section.innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          // The call was aborted because a newer search term took over
          return;
        }
        this.close();
        throw error;
      });
  }

    sliderCall() {
    // Initialise Swiper on any new product card carousels injected into search results
    if (typeof initProductCardSwipers === 'function') {
      initProductCardSwipers();
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.closest(".details").querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.closest(".details").getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.closest(".details").querySelector(".search-modal.modal__content").setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.sliderCall();
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.closest(".details").querySelector(".search-modal.modal__content").removeAttribute('loading');
    this.setLiveRegionText(this.closest(".details").querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  // getResultsMaxHeight() {
  //   this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
  //   return this.resultsMaxHeight;
  // }

  open() {
    // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.closest(".details").querySelector(".search-modal.modal__content").setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('search-results-open');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.closest(".details").querySelector(".search-modal.modal__content").removeAttribute('loading');
    this.closest(".details").querySelector(".search-modal.modal__content").removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('search-results-open');
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);